import { DEFAULT_VIEW, type GroupKey, type SortKey, type TicketView } from "./ticketView.js";

/**
 * Filter-Presets der Ticketliste: eine gespeicherte Leisteneinstellung
 * (Status, Typ, Ersteller, Bearbeiter, Sortierung, Gruppierung) unter einem
 * Namen. Die Schnellsuche `q` gehört bewusst NICHT dazu — sie ist eine
 * Momentaufnahme, kein Filter, den man wiederholt.
 *
 * Reine Funktionen; der Zustand (localStorage) liegt in `stores/viewPresets.ts`.
 * Presets sind je Browser, nicht je Konto: Sie brauchen keine Tabelle, keine
 * Route und keinen Audit-Eintrag — und ein Lead richtet sie sich an seinem
 * Arbeitsplatz ein, so wie die „Besprochen"-Haken (`stores/reviewed.ts`).
 */
export type PresetView = Omit<TicketView, "q">;

export interface ViewPreset {
  id: string;
  name: string;
  view: PresetView;
}

export const PRESET_NAME_MAX = 60;

const SORT_KEYS: SortKey[] = ["updated", "created", "status", "type", "key"];
const GROUP_KEYS: GroupKey[] = ["none", "status", "type", "reporter"];

/** Die speicherbaren Teile einer Sicht — ohne `q`. */
export function presetViewOf(view: TicketView): PresetView {
  return {
    excludedStatus: [...view.excludedStatus],
    excludedTypes: [...view.excludedTypes],
    reporters: [...view.reporters],
    assignees: [...view.assignees],
    sort: view.sort,
    group: view.group,
  };
}

/** Preset anwenden: die Filter des Presets, die laufende Suche bleibt. */
export function applyPreset(preset: ViewPreset, current: TicketView): TicketView {
  return { ...current, ...presetViewOf({ ...preset.view, q: "" }) };
}

/**
 * Preset hinzufügen. Ein vorhandener Name wird ERSETZT statt verdoppelt —
 * „Meine offenen" zweimal in der Liste wäre eine Falle. Namen werden
 * getrimmt und gedeckelt; ein leerer Name ergibt die Liste unverändert.
 */
export function addPreset(list: ViewPreset[], name: string, view: TicketView, id: string = crypto.randomUUID()): ViewPreset[] {
  const clean = name.trim().slice(0, PRESET_NAME_MAX);
  if (clean === "") return list;
  const next: ViewPreset = { id, name: clean, view: presetViewOf(view) };
  const idx = list.findIndex((p) => p.name.localeCompare(clean, "de", { sensitivity: "accent" }) === 0);
  if (idx === -1) return [...list, next];
  const copy = [...list];
  copy[idx] = { ...next, id: list[idx]!.id };
  return copy;
}

export function removePreset(list: ViewPreset[], id: string): ViewPreset[] {
  return list.filter((p) => p.id !== id);
}

/** Trifft das Preset die aktuelle Sicht (ohne `q`) genau? — für die Markierung im Menü. */
export function presetMatches(preset: ViewPreset, view: TicketView): boolean {
  const a = preset.view;
  const same = (x: string[], y: string[]) => x.length === y.length && [...x].sort().every((v, i) => v === [...y].sort()[i]);
  return (
    same(a.excludedStatus, view.excludedStatus) &&
    same(a.excludedTypes, view.excludedTypes) &&
    same(a.reporters, view.reporters) &&
    same(a.assignees, view.assignees) &&
    a.sort === view.sort &&
    a.group === view.group
  );
}

const strList = (v: unknown): string[] | null =>
  Array.isArray(v) && v.every((x) => typeof x === "string") ? (v as string[]) : null;

/**
 * Gespeicherte Presets lesen — fail-closed je Eintrag: Was nicht die
 * erwartete Form hat, fällt weg, der Rest bleibt. Unbekannte Sortier- oder
 * Gruppierungsschlüssel fallen auf die Vorgabe zurück statt die Liste zu
 * verwerfen.
 */
export function parsePresets(raw: unknown): ViewPreset[] {
  if (!Array.isArray(raw)) return [];
  const out: ViewPreset[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const p = item as Record<string, unknown>;
    const v = p["view"];
    if (typeof p["id"] !== "string" || typeof p["name"] !== "string" || !v || typeof v !== "object") continue;
    const view = v as Record<string, unknown>;
    const excludedStatus = strList(view["excludedStatus"]);
    const excludedTypes = strList(view["excludedTypes"]);
    const reporters = strList(view["reporters"]);
    const assignees = strList(view["assignees"]);
    if (!excludedStatus || !excludedTypes || !reporters || !assignees) continue;
    const sort = SORT_KEYS.includes(view["sort"] as SortKey) ? (view["sort"] as SortKey) : DEFAULT_VIEW.sort;
    const group = GROUP_KEYS.includes(view["group"] as GroupKey) ? (view["group"] as GroupKey) : DEFAULT_VIEW.group;
    const name = p["name"].trim().slice(0, PRESET_NAME_MAX);
    if (name === "") continue;
    out.push({ id: p["id"], name, view: { excludedStatus, excludedTypes, reporters, assignees, sort, group } });
  }
  return out;
}
